import {  Component,  OnInit,  TemplateRef,  ViewChild} from '@angular/core';
import { FormArray, FormBuilder,  FormGroup, Validators} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ScreeningService } from 'src/app/common-methods/services/screening.service';
import { CommonService } from 'src/app/common-methods/services/common.service';
import { AutomationService } from 'src/app/common-methods/services/automation.service';
import { AuthService } from 'src/app/common-methods/services/auth.service';

@Component({
  selector: 'app-candidate-insuff',
  templateUrl: './candidate-insuff.component.html',
  styleUrls: ['./candidate-insuff.component.css']
})

export class CandidateInsuffComponent implements OnInit {

  screeningId: any;
  token: string = '';
  candidateData: any;
  candidateInsuffList: any[] = [];
  loading = false;
  insuffForm: FormGroup;

  @ViewChild('sucessAlert', null)
  sucessAlert: TemplateRef<any>;

  @ViewChild('unavailableDialog', null)
  unavailableDialog: TemplateRef<any>;

  headerText: string;
  bodyText: string;
  alertEmail: string = '';
  isSubmitted = false;
  isInvalidToken = false;
  invalidTokenMessage = '';

  // When true (draft-save or partial-submit), dialogClose() swaps the form out for a static
  // confirmation screen instead of returning to it — same idea as isSubmitted (full success/
  // already-cleared), just reusing headerText/bodyText from whichever action just completed.
  actionResultShown = false;

  expandedStates: boolean[] = [];
  submitAttempted = false;

  maxExpectedDate: string = '';

  currentUnavailableCompIndex = -1;
  currentUnavailableDocIndex: number | null = null; // null = whole-component (legacy flow), number = per-document-type
  tempUnavailableReason = '';
  tempUnavailableReasonType = '';
  tempExpectedDate = '';
  unavailableReasons: string[] = [
  'Company Closed',
  'Document Not Issued',
  'Document Lost',
  'Will Provide Later',
  'Others'
   ];
  // Only a reason implying the candidate will still get the document later needs an expected
  // date - the rest ("Company Closed", "Document Lost", etc.) mean it can never be provided.
  reasonsRequiringExpectedDate: string[] = ['Will Provide Later', 'Document Not Issued'];
  tempOtherReason = '';

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private automation: AutomationService,
    private authService: AuthService,
    private router: Router,
    private screeningService: ScreeningService,
    public common: CommonService
  ) { }

  ngOnInit() {

    this.buildForm();
    this.computeMaxExpectedDate();

    this.route.queryParams.subscribe(par => {

      this.screeningId = par.screeningId;
      this.token = par.token;

      if (this.token) {
        this.getCandidateInsuffDetails();
      }

    });

  }

  private computeMaxExpectedDate() {

    const max = new Date();
    max.setDate(max.getDate() + 15);
    const mm = String(max.getMonth() + 1).padStart(2, '0');
    const dd = String(max.getDate()).padStart(2, '0');
    this.maxExpectedDate = max.getFullYear() + '-' + mm + '-' + dd;

  }

  buildForm() {

    this.insuffForm = this.fb.group({
      components: this.fb.array([])
    });

  }

  get componentArray(): FormArray {
    return this.insuffForm.controls['components'] as FormArray;
  }

  get resolvedCount(): number {
    return this.componentArray.controls.filter(c => this.hasResponse(c)).length;
  }

  // Drives the "Where you are in the process" stepper — true only once every card is
  // genuinely resolved (real uploads / filled information), with nothing marked
  // unavailable or partially available. Any such flag keeps the stepper on
  // "Info needed from you" instead of advancing to "Being verified".
  get isEverythingCleanlyResolved(): boolean {
    return this.componentArray.length > 0
      && this.componentArray.controls.every(c => this.hasResponse(c) && !this.needsAttentionResolution(c));
  }

  getDocumentEntries(control: any): FormArray {
    return control.get('documentEntries') as FormArray;
  }

  hasTypeResponse(docEntry: any): boolean {
    const v = docEntry.value;
    const hasNewFiles = v.insuffDocument && v.insuffDocument.length > 0;
    const hasDraftFiles = v.draftDocuments && v.draftDocuments.length > 0;
    const isUnavailable = v.documentUnavailable === true && v.unavailableReason && v.unavailableReason.trim();
    return hasNewFiles || hasDraftFiles || !!isUnavailable || !!v.isAlreadySubmitted;
  }

  hasResponse(control: any): boolean {
    const v = control.value;
    const types: string[] = v.insuffTypes || [];
    const typesJoined = types.join('');

    if (typesJoined.includes('Information') && !(v.information && v.information.trim())) {
      return false;
    }

    if (typesJoined.includes('Document')) {
      const docEntries = this.getDocumentEntries(control);

      if (docEntries && docEntries.length > 0) {
        if (!docEntries.controls.every(e => this.hasTypeResponse(e))) { return false; }
      } else {
        const hasNewFiles = v.insuffDocument && v.insuffDocument.length > 0;
        const hasDraftFiles = v.draftDocuments && v.draftDocuments.length > 0;
        const isUnavailable = v.documentUnavailable && v.unavailableReason && v.unavailableReason.trim();
        if (!hasNewFiles && !hasDraftFiles && !isUnavailable) { return false; }
      }
    }

    return true;
  }

  toggleCard(index: number) {
    this.expandedStates[index] = !this.expandedStates[index];
  }

  isUnavailableResolution(control: any): boolean {
    const v = control.value;
    const types: string[] = v.insuffTypes || [];
    const typesJoined = types.join('');
    const hasDocType = typesJoined.includes('Document');
    if (!hasDocType) { return false; }

    const docEntries = this.getDocumentEntries(control);
    if (docEntries && docEntries.length > 0) {
      // Only "fully unavailable" (every type marked unavailable, nothing uploaded) counts —
      // a mix of uploaded + unavailable types is reported separately as "partially available".
      return docEntries.controls.every(e => {
        const ev = e.value;
        const hasFiles = (ev.insuffDocument && ev.insuffDocument.length > 0) || (ev.draftDocuments && ev.draftDocuments.length > 0);
        return !hasFiles && ev.documentUnavailable === true && ev.unavailableReason && ev.unavailableReason.trim();
      });
    }

    const hasNewFiles = v.insuffDocument && v.insuffDocument.length > 0;
    const hasDraftFiles = v.draftDocuments && v.draftDocuments.length > 0;
    return !hasNewFiles && !hasDraftFiles
      && v.documentUnavailable && v.unavailableReason && v.unavailableReason.trim();
  }

  isPartiallyAvailableResolution(control: any): boolean {
    const docEntries = this.getDocumentEntries(control);
    if (!docEntries || docEntries.length === 0) { return false; }

    const anyUploaded = docEntries.controls.some(e => {
      const ev = e.value;
      return (ev.insuffDocument && ev.insuffDocument.length > 0) || (ev.draftDocuments && ev.draftDocuments.length > 0);
    });
    const anyUnavailable = docEntries.controls.some(e => e.value.documentUnavailable === true);

    return anyUploaded && anyUnavailable;
  }

  // True when the resolution isn't a clean "everything added normally" outcome —
  // either fully marked unavailable, or a mix of uploaded + unavailable document types.
  // Drives the amber (vs. green) styling everywhere a card's resolved state is shown.
  needsAttentionResolution(control: any): boolean {
    return this.isUnavailableResolution(control) || this.isPartiallyAvailableResolution(control);
  }

  getStatusLabel(control: any): string {
    const types: string[] = control.value.insuffTypes || [];
    const responded = this.hasResponse(control);

    if (types.length === 0) { return responded ? 'Response added' : 'Response required'; }

    const label = types.map(t => t.replace(/\s*required\s*$/i, '')).join(' & ');

    if (!responded) { return label + ' required'; }

    if (this.isPartiallyAvailableResolution(control)) { return 'Partially available'; }

    if (!this.isUnavailableResolution(control)) { return label + ' added'; }

    const hasInfoType = types.join('').includes('Information');
    if (hasInfoType) { return 'Information added · Document unavailable'; }

    return 'Marked unavailable';
  }

  getCandidateInsuffDetails() {

    this.loading = true;

    this.automation
      .getCandidateInsuffByToken(this.token)
      .subscribe({

        next: (response: any) => {

          this.loading = false;

          if (response && response.isValid) {

            this.candidateData = response.data;
            this.alertEmail = (this.candidateData && this.candidateData.camEmail)
              ? this.candidateData.camEmail : '';

            const components = response.data.components || [];

            // All components cleared by ops — empty array, show popup
            if (components.length === 0) {
              this.isSubmitted = true;
              this.headerText = 'All Cleared';
              this.bodyText = (response.message)
                ? response.message
                : 'All insufficiencies for this case have already been cleared. No further action is required.';
              this.dialog.open(this.sucessAlert, { width: '440px' });
              return;
            }

            // Candidate already submitted via portal
            if (this.candidateData && this.candidateData.isSubmitted) {
              this.isSubmitted = true;
              return;
            }

            // Normal flow — render form
            this.candidateInsuffList = components;
            this.componentArray.clear();
            this.expandedStates = [];

            this.candidateInsuffList.forEach(x => {

              const insuffTypes: string[] = Array.isArray(x.insuffTypes)
                ? x.insuffTypes
                : (x.insuffTypes ? [x.insuffTypes] : []);

              const documentTypes: any[] = x.documentTypes || [];
              const unavailableDocs: any[] = x.unavailableDocuments || [];
              const draftDocs: any[] = x.draftDocuments || [];

              // Per-document-type sub-cards — only built when this insufficiency has named
              // document rows (documentTypes non-empty). Each type tracks its own upload/
              // unavailable state independently, matching the "separate reason per type" flow.
              const documentEntries = documentTypes.map(t => {
                const draftForType = draftDocs.filter(d => d.insuffDocTransId === t.insuffDocTransId);
                const unavailableForType = unavailableDocs.find(u => u.insuffDocTransId === t.insuffDocTransId);
                // Promoted to permanent storage by an earlier partial submission - DraftDocumentsJson
                // no longer carries it, so show it read-only instead of pending/re-uploadable.
                const alreadySubmitted = !!t.isAlreadySubmitted;

                return this.fb.group({
                  insuffDocTransId:      [t.insuffDocTransId],
                  description:           [t.description],
                  isAlreadySubmitted:    [alreadySubmitted],
                  submittedFileName:     [t.submittedFileName || ''],
                  documentUnavailable:   [alreadySubmitted
                    ? false
                    : (unavailableForType ? true : (draftForType.length > 0 ? false : null))],
                  unavailableReason:     [unavailableForType ? unavailableForType.reason : ''],
                  unavailableReasonType: [unavailableForType ? unavailableForType.reasonType : ''],
                  expectedDate:          [unavailableForType ? this.toDateInput(unavailableForType.expectedDate) : ''],
                  draftDocuments:        [draftForType],
                  insuffDocument:        [[]],
                });
              });

              this.componentArray.push(

                this.fb.group({

                  screeningCompId:    [x.screeningCompId],
                  insufficiencyId:    [x.insufficiencyId],
                  componentName:      [x.componentName],
                  functionalEntity:   [x.functionalEntity],
                  insufficiencyLevel: [x.insufficiencyLevel],
                  insuffTypes:        [insuffTypes],
                  insuffRaisedDate:   [x.insuffRaisedDate],
                  remarks:            [x.remarks],
                  information:        [x.draftInformationText || ''],
                  // Tri-state: true = marked unavailable, false = candidate explicitly chose "I can upload
                  // these" (or already has draft activity from a prior visit), null = not yet decided.
                  // Only meaningful when documentEntries is empty (no named document types).
                  documentUnavailable:[x.isUnavailable === true
                    ? true
                    : ((x.draftDocuments && x.draftDocuments.length > 0) ? false : null)],
                  unavailableReason:  [x.unavailableReason || ''],
                  expectedDate:       [this.toDateInput(x.expectedDate)],
                  draftDocuments:     [x.draftDocuments || []],
                  insuffDocument:     [[]],
                  unavailableReasonType:[x.unavailableReasonType || ''],
                  documentEntries:    this.fb.array(documentEntries),
                })

              );

              this.expandedStates.push(true);

            });

          } else {

            this.isInvalidToken = true;
            this.invalidTokenMessage = (response && response.message)
              ? response.message
              : 'This link is invalid or has expired. Please use the link sent to your email.';

          }

        },

        error: () => {

          this.loading = false;
          this.isInvalidToken = true;
          this.invalidTokenMessage = 'Unable to verify your link. Please check your connection and try again, or contact support.';

        }

      });

  }

  openUploadDoc(event, index) {

    const control = this.componentArray.at(index);
    this.addFilesToControl(control, event.target.files);

  }

  // Each document-type card represents exactly one named document — only one file is
  // kept per type (a new selection replaces the previous one), unlike the flat/legacy
  // dropzone which lumps multiple unnamed documents together and so allows multiple files.
  openTypeUploadDoc(event, compIndex: number, docIndex: number) {

    const files: FileList = event.target.files;
    if (!files || files.length === 0) { return; }

    const file = files[0];
    const fileSize = file.size / (1024 * 1024);

    if (fileSize > 5) {
      this.showSnackbar('Maximum file size is 5 MB', 'warning-snackbar');
      return;
    }

    const control = this.getDocumentEntries(this.componentArray.at(compIndex)).at(docIndex);
    control.patchValue({ insuffDocument: [{ fileName: file.name, document1: file }] });

  }

  private addFilesToControl(control: any, files: FileList) {

    if (!files || files.length === 0) { return; }

    const currentDocs = control.get('insuffDocument').value;
    let oversized = false;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const fileSize = file.size / (1024 * 1024);

      if (fileSize > 5) {
        oversized = true;
        continue;
      }

      currentDocs.push({ fileName: file.name, document1: file });
    }

    if (oversized) {
      this.showSnackbar('Maximum file size is 5 MB. Larger files were skipped.', 'warning-snackbar');
    }

    control.patchValue({ insuffDocument: currentDocs });

  }

  removeDocument(index, docIndex) {

    const control = this.componentArray.at(index);
    const docs = control.get('insuffDocument').value;
    docs.splice(docIndex, 1);
    control.patchValue({ insuffDocument: docs });

  }

  removeTypeDocument(compIndex: number, typeIndex: number, docIndex: number) {

    const control = this.getDocumentEntries(this.componentArray.at(compIndex)).at(typeIndex);
    const docs = control.get('insuffDocument').value;
    docs.splice(docIndex, 1);
    control.patchValue({ insuffDocument: docs });

  }

  removeDraftDocument(index: number, docIndex: number) {

    const control = this.componentArray.at(index);
    const draftCtrl = control.get('draftDocuments');
    const docs: any[] = (draftCtrl ? draftCtrl.value || [] : []).slice();
    docs.splice(docIndex, 1);
    control.patchValue({ draftDocuments: docs });

  }

  removeTypeDraftDocument(compIndex: number, typeIndex: number, docIndex: number) {

    const control = this.getDocumentEntries(this.componentArray.at(compIndex)).at(typeIndex);
    const draftCtrl = control.get('draftDocuments');
    const docs: any[] = (draftCtrl ? draftCtrl.value || [] : []).slice();
    docs.splice(docIndex, 1);
    control.patchValue({ draftDocuments: docs });

  }

  previewDocument(doc: any) {

    const url = URL.createObjectURL(doc.document1);
    window.open(url, '_blank');

  }

  private toRelativeDraftPath(filePath: string): string {
    if (!filePath) { return filePath; }
    const idx = filePath.toLowerCase().indexOf('candidateinsuffdraft');
    return idx > -1 ? filePath.substring(idx) : filePath;
  }

  viewDraftDocument(doc: any) {

    this.automation.downloadDraftDocument(this.token, this.toRelativeDraftPath(doc.filePath))
      .subscribe({
        next: (buffer: ArrayBuffer) => {
          const mimeType = this.getMimeType(doc.fileName);
          const blob = new Blob([buffer], { type: mimeType });
          const url = URL.createObjectURL(blob);
          window.open(url, '_blank');
        },
        error: () => {
          this.showSnackbar('Unable to load the document. Please try again.', 'warning-snackbar');
        }
      });

  }

  downloadDraftDocument(doc: any) {

    this.automation.downloadDraftDocument(this.token, this.toRelativeDraftPath(doc.filePath))
      .subscribe({
        next: (buffer: ArrayBuffer) => {
          const mimeType = this.getMimeType(doc.fileName);
          const blob = new Blob([buffer], { type: mimeType });
          const url = URL.createObjectURL(blob);
          const anchor = document.createElement('a');
          anchor.href = url;
          anchor.download = doc.fileName;
          anchor.click();
          URL.revokeObjectURL(url);
        },
        error: () => {
          this.showSnackbar('Unable to download the document. Please try again.', 'warning-snackbar');
        }
      });

  }

  private getMimeType(fileName: string): string {
    const ext = (fileName.split('.').pop() || '').toLowerCase();
    const map: any = {
      pdf:  'application/pdf',
      png:  'image/png',
      jpg:  'image/jpeg',
      jpeg: 'image/jpeg'
    };
    return map[ext] || 'application/octet-stream';
  }

  allowSameFileUpload(event: any): void {

    event.target.value = '';

  }

  // docIndex omitted (null) = legacy whole-component control; a number targets
  // documentEntries[docIndex] on that component instead (separate reason per document type).
  private getUnavailableTargetControl(): any {
    const compControl = this.componentArray.at(this.currentUnavailableCompIndex);
    if (this.currentUnavailableDocIndex === null) { return compControl; }
    return this.getDocumentEntries(compControl).at(this.currentUnavailableDocIndex);
  }

  onDocumentUnavailableChange(event: any, compIndex: number, docIndex: number | null = null) {

    this.currentUnavailableCompIndex = compIndex;
    this.currentUnavailableDocIndex = docIndex;
    const control = this.getUnavailableTargetControl();

    if (event.checked) {

      control.patchValue({ insuffDocument: [] });
      this.tempUnavailableReason = control.get('unavailableReason').value || '';
      this.tempExpectedDate = control.get('expectedDate').value || '';
      this.tempUnavailableReasonType = control.get('unavailableReasonType').value || '';
      this.dialog.open(this.unavailableDialog, { width: '480px', disableClose: true });

    } else {

      control.patchValue({ unavailableReason: '', unavailableReasonType: '', expectedDate: null });
      this.tempUnavailableReason = '';
      this.tempUnavailableReasonType = '';
      this.tempExpectedDate = '';
    }

  }

  editUnavailable(compIndex: number, docIndex: number | null = null) {

    this.currentUnavailableCompIndex = compIndex;
    this.currentUnavailableDocIndex = docIndex;
    const control = this.getUnavailableTargetControl();
    this.tempUnavailableReason = control.get('unavailableReason').value || '';
    this.tempExpectedDate = control.get('expectedDate').value || '';
    this.tempUnavailableReasonType = control.get('unavailableReasonType').value || '';
    this.dialog.open(this.unavailableDialog, { width: '480px', disableClose: true });

  }

  isExpectedDateRequired(reasonType: string): boolean {
    return this.reasonsRequiringExpectedDate.indexOf(reasonType) !== -1;
  }

  confirmUnavailable() {

    if (!this.tempUnavailableReason || !this.tempUnavailableReason.trim()) {
      this.showSnackbar('Please enter a reason for unavailability', 'warning-snackbar');
      return;
    }
 	if (!this.tempUnavailableReasonType || !this.tempUnavailableReasonType.trim()) {
      this.showSnackbar('Please select a reason category', 'warning-snackbar');
      return;
    }
    const dateRequired = this.isExpectedDateRequired(this.tempUnavailableReasonType);
    if (dateRequired && !this.tempExpectedDate) {
      this.showSnackbar('Please select the expected date of submission', 'warning-snackbar');
      return;
    }

    if (this.tempExpectedDate) {
      const selected = new Date(this.tempExpectedDate);
      const maxDate = new Date();
      maxDate.setDate(maxDate.getDate() + 15);
      maxDate.setHours(23, 59, 59, 999);

      if (selected > maxDate) {
        this.showSnackbar('Expected date cannot be more than 15 days from today', 'warning-snackbar');
        return;
      }
    }

    const control = this.getUnavailableTargetControl();
    control.patchValue({
      unavailableReason: this.tempUnavailableReason,
      // Not mandatory for every reason, but still optional/fillable — keep whatever the
      // candidate entered either way instead of discarding it when it wasn't required.
      expectedDate: this.tempExpectedDate || null,
      unavailableReasonType: this.tempUnavailableReasonType
    });

    this.dialog.closeAll();

  }

  cancelUnavailable() {

    const control = this.getUnavailableTargetControl();
    if (!control.get('unavailableReason').value) {
      control.patchValue({ documentUnavailable: null, unavailableReason: '',
      unavailableReasonType: '',
      expectedDate: null });

      this.tempUnavailableReason = '';
      this.tempUnavailableReasonType = '';
      this.tempExpectedDate = '';
    }
    this.dialog.closeAll();

  }

  saveDraft() {

    this.submitInsuff(true);

  }

  finalSubmit() {

    this.submitAttempted = true;

    this.componentArray.controls.forEach((control: any, idx: number) => {
      if (!this.hasResponse(control)) {
        this.expandedStates[idx] = true;
      }
    });

    if (!this.validateForSubmit()) { return; }

    this.submitInsuff(false);

  }

  private validateForSubmit(): boolean {

    const errors: string[] = [];

    this.componentArray.controls.forEach((control: any, idx: number) => {
      const v = control.value;
      const types: string[] = v.insuffTypes || [];
      const typesJoined = types.join('');
      const name = v.componentName || ('Item ' + (idx + 1));
      const missing: string[] = [];

      if (typesJoined.includes('Information')) {
        if (!v.information || !v.information.trim()) {
          missing.push('information');
        }
      }

      if (typesJoined.includes('Document')) {
        const docEntries = this.getDocumentEntries(control);

        if (docEntries && docEntries.length > 0) {
          const unresolved = docEntries.controls.filter(e => !this.hasTypeResponse(e));
          if (unresolved.length > 0) {
            const names = unresolved.map(e => e.value.description).join(', ');
            missing.push(`an upload (or mark as unavailable) for: ${names}`);
          }
        } else {
          const hasNewFiles = v.insuffDocument && v.insuffDocument.length > 0;
          const hasDraftFiles = v.draftDocuments && v.draftDocuments.length > 0;
          const isUnavailable = v.documentUnavailable && v.unavailableReason && v.unavailableReason.trim();
          if (!hasNewFiles && !hasDraftFiles && !isUnavailable) {
            missing.push('a document upload (or mark it as unavailable)');
          }
        }
      }

      if (missing.length > 0) {
        const verb = missing.length > 1 ? 'are' : 'is';
        errors.push(name + ': ' + missing.join(' and ') + ' ' + verb + ' required');
      }
    });

    if (errors.length > 0) {
      this.showSnackbar(errors[0], 'warning-snackbar');
      return false;
    }

    return true;

  }

  submitInsuff(isDraft: boolean) {

    this.loading = true;

    const formData = new FormData();
    const entries: any[] = [];
    const allFiles: File[] = [];

    this.componentArray.controls.forEach((control: any) => {

      const value = control.value;
      const docEntries: any[] = value.documentEntries || [];

      let isUnavailable: boolean;
      let unavailableReason: string | null = null;
      let unavailableReasonType: string | null = null;
      let expectedDate: string | null = null;
      const uploadedDocument: any[] = [];
      let keptDraftDocuments: string[] = [];
      let unavailableDocuments: any[] | null = null;

      if (docEntries.length > 0) {

        // Per-document-type flow — never a single whole-component flag; each type's
        // own upload/unavailable state is reported individually via the arrays below.
        isUnavailable = false;

        unavailableDocuments = docEntries
          .filter(e => e.documentUnavailable === true)
          .map(e => ({
            insuffDocTransId: e.insuffDocTransId,
            reason: e.unavailableReason,
            reasonType: e.unavailableReasonType,
            expectedDate: e.expectedDate || null
          }));
        if (unavailableDocuments.length === 0) { unavailableDocuments = null; }

        docEntries.forEach(e => {
          const docs: Array<{ fileName: string; document1: File }> = e.insuffDocument || [];
          docs.forEach(doc => {
            allFiles.push(doc.document1);
            uploadedDocument.push({ fileName: doc.fileName, insuffDocTransId: e.insuffDocTransId });
          });

          const keptForType = (!isDraft && e.documentUnavailable)
            ? []
            : (e.draftDocuments || []).map((d: any) => this.toRelativeDraftPath(d.filePath));
          keptDraftDocuments = keptDraftDocuments.concat(keptForType);
        });

      } else {

        isUnavailable = value.documentUnavailable === true;
        unavailableReason = value.unavailableReason || null;
        unavailableReasonType = value.unavailableReasonType || null;
        expectedDate = value.expectedDate || null;

        const docs: Array<{ fileName: string; document1: File }> = value.insuffDocument || [];
        docs.forEach(doc => {
          allFiles.push(doc.document1);
          uploadedDocument.push({ fileName: doc.fileName });
        });

        keptDraftDocuments = (!isDraft && value.documentUnavailable)
          ? []
          : (value.draftDocuments || []).map((d: any) => this.toRelativeDraftPath(d.filePath));
      }

      entries.push({
        insufficiencyId:    value.insufficiencyId,
        insuffCategory:     value.insuffTypes,
        isUnavailable,
        unavailableReason,
        expectedDate,
        unavailableReasonType,
        informationText:    value.information || null,
        uploadedDocument,
        keptDraftDocuments,
        unavailableDocuments
      });

    });

    const submitVm = {
      token: this.token,
      isDraft,
      createdUserId: (this.candidateData && this.candidateData.userId) ? this.candidateData.userId : 0,
      entries
    };

    formData.append('submitVm', JSON.stringify(submitVm));
    allFiles.forEach(file => formData.append('files', file));

    const apiCall = isDraft
      ? this.automation.saveDraft(formData)
      : this.automation.submitInsuffForm(formData);

    apiCall.subscribe({

      next: (response: any) => {

        this.loading = false;

        if (response && response.isValid) {

          this.alertEmail = (this.candidateData && this.candidateData.camEmail)
            ? this.candidateData.camEmail : '';

          const dateLabel = response.pendingDeadline
            ? this.formatDate(new Date(response.pendingDeadline))
            : 'the due date';

          if (isDraft) {

            this.headerText = 'Draft Saved';

            if (response.isPartial && response.pendingTypeLabel) {

              this.bodyText =
                'Draft saved successfully. However, the \'' + response.pendingTypeLabel + '\' remains pending. ' +
                'We request you to submit the same on or before ' + dateLabel + '. ' +
                'For any modifications, please contact';

            } else {

              this.bodyText =
                'Draft saved successfully. Please complete all required fields and submit. ' +
                'For any modifications, please contact';

            }

          } else if (response.isPartial) {

            // Some component was submitted as "currently unavailable" (or another insufficiency for
            // this candidate is still outstanding) - it is NOT actually cleared yet, so don't claim
            // full success or lock the form; the candidate can still come back and resolve it.
            this.headerText = 'Submitted';
            this.bodyText =
              'Your response has been submitted. However, the \'' + (response.pendingTypeLabel || 'response') + '\' remains pending. ' +
              'We request you to submit the same on or before ' + dateLabel + '. ' +
              'For any modifications, please contact';

          } else {

            this.isSubmitted = true;
            this.headerText = 'Success';
            this.bodyText = 'Submission successful. For any modifications, contact';

          }

          this.dialog.open(this.sucessAlert, { width: '440px' });

        } else {

          this.showSnackbar(
            (response && response.message) ? response.message : 'Something went wrong',
            'warning-snackbar'
          );

        }

      },

      error: () => {

        this.loading = false;
        this.showSnackbar('Request failed. Please try again.', 'warning-snackbar');

      }

    });

  }

  private toDateInput(isoDate: string): string {
    if (!isoDate) { return ''; }
    return isoDate.substring(0, 10);
  }

  // Display-only — expectedDate is stored/bound as yyyy-mm-dd (for the native date
  // input); the unavailability summary shows it as dd-mm-yyyy instead.
  toDisplayDate(dateStr: string): string {
    if (!dateStr) { return dateStr; }
    const parts = dateStr.substring(0, 10).split('-');
    return parts.length === 3 ? `${parts[2]}-${parts[1]}-${parts[0]}` : dateStr;
  }

  private formatDate(date: Date): string {

    const d = String(date.getDate()).padStart(2, '0');
    const m = String(date.getMonth() + 1).padStart(2, '0');
    return d + '/' + m + '/' + date.getFullYear();

  }

  showSnackbar(message, css) {

    this.snackBar.open(message, 'Close', {
      duration: 3000,
      panelClass: [css],
      verticalPosition: 'top',
      horizontalPosition: 'center'
    });

  }

  dialogClose() {

    this.dialog.closeAll();

    // Swap the form out for the static confirmation screen instead of returning to it —
    // isSubmitted already covers the full-success/already-cleared case on its own.
    if (!this.isSubmitted) {
      this.actionResultShown = true;
    }

  }

}
